import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { BadgeComponent } from '../../../../shared/components/badge/badge';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { UsersApiService } from '../../../../core/services/users/users-api.service';
import { TelecallersApiService } from '../../../../core/services/telecallers/telecallers-api.service';
import { DashboardApiService } from '../../../../core/services/dashboard/dashboard-api.service';
import {
  MedicineBreakdownItem,
  HqPerformanceItem,
  ManagerHqPerformanceItem,
  DoctorPerformanceItem,
} from '../../../../core/services/reports/reports-api.service';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton-loader/skeleton-loader.component';
import { StatsCardComponent } from '../../../../shared/components/stats-card/stats-card';
import { EmptyStateComponent } from '../../../../shared/components/empty-state/empty-state.component';
import { HqApiService } from '../../../../core/services/headquarters/hq-api.service';
import { LookupDataService } from '../../../../core/services/lookup-data.service';
import { Subject } from 'rxjs';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { takeUntil } from 'rxjs/operators';
import { FiltersComponent } from '../../../../shared/components/filters.component';

// User Interface
export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

// Telecaller Interface
export interface Telecaller {
  id: string;
  user_id: string;
  name: string;
  programs: string[];
  displayPrograms: string[];
  morePrograms: number;
  patientsAssigned: number;
  programCount: number;
  totalSales: string;

}

// Stats Interface
export interface DashboardStats {
  totalPatients: number;
  enrolledPatients: number;
  enrollmentRate: number;
  medicineOrders: number;
  testBookings: number;
  activeMrs: number;
  totalMrs: number;
  totalDoctors: number;
  totalHospitals: number;
  totalMedications: number;
  currentMonthRevenue: string;
  avgComplianceRate: number;
}

@Component({
  selector: 'app-organization-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, BadgeComponent, IconComponent, SkeletonLoaderComponent, StatsCardComponent, FiltersComponent, EmptyStateComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
})
export class OrganizationDashboardComponent implements OnInit, OnDestroy {
  // Subscription management
  private destroy$ = new Subject<void>();

  // Dashboard Stats
  stats: DashboardStats = {
    totalPatients: 0,
    enrolledPatients: 0,
    enrollmentRate: 0,
    medicineOrders: 0,
    testBookings: 0,
    activeMrs: 0,
    totalMrs: 0,
    totalDoctors: 0,
    totalHospitals: 0,
    totalMedications: 0,
    currentMonthRevenue: '₹0',
    avgComplianceRate: 0,
  };
  isLoadingStats = true;
  isInitialLoad = true;

  // Breakdown lists
  medicineWisePatients: MedicineBreakdownItem[] = [];
  hqWisePatients: HqPerformanceItem[] = [];
  managerHqWisePatients: ManagerHqPerformanceItem[] = [];
  drWisePatients: DoctorPerformanceItem[] = [];

  // Filters
  filters = {
    zone: '',
    hq: '',
    state: '',
    city: '',
    mr: '',
    period: null,
    start_date: '',
    end_date: ''
  };

  // Users data
  users: User[] = [];
  isLoadingUsers = true;

  // Mock data
  telecallers: Telecaller[] = [];
  isLoadingTelecallers = true;
  filteredTelecallers: Telecaller[] = [];

  // Role flags
  isSalesMr = false;

  constructor(
    private usersService: UsersApiService,
    private telecallersService: TelecallersApiService,
    private dashboardService: DashboardApiService,
    private hqService: HqApiService,
    private lookupService: LookupDataService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.isSalesMr = this.authService.hasRoleName('SALES EXECUTIVE (MR)');
    this.fetchDashboardSummary();
    this.fetchUsers();
    this.fetchTelecallers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkLoadingState() {
    if (!this.isLoadingUsers && !this.isLoadingStats && !this.isLoadingTelecallers) {
      this.isInitialLoad = false;
    }
  }

  onFilterChange() {
    this.fetchDashboardSummary();
  }

  resetFilters() {
    this.filters = {
      zone: '',
      hq: '',
      state: '',
      city: '',
      mr: '',
      period: null,
      start_date: '',
      end_date: ''
    };
    this.fetchDashboardSummary();
  }

  fetchDashboardSummary() {
    this.isLoadingStats = true;

    // Create a copy of filters to format dates for API
    const apiFilters = { ...this.filters };

    if (apiFilters.period === 'custom') {
      if (apiFilters.start_date) {
        apiFilters.start_date = `${apiFilters.start_date}T00:00:00.000Z`;
      }
      if (apiFilters.end_date) {
        apiFilters.end_date = `${apiFilters.end_date}T23:59:59.999Z`;
      }
    } else {
      // Clear custom dates if period is not custom
      apiFilters.start_date = '';
      apiFilters.end_date = '';
    }

    this.dashboardService.getDashboardOverview(apiFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.stats = {
            totalPatients: response.total_patients || 0,
            enrolledPatients: response.enrolled_patients || 0,
            enrollmentRate: response.enrollment_rate || 0,
            medicineOrders: response.medicine_orders || 0,
            testBookings: response.test_bookings || 0,
            activeMrs: response.active_mrs || 0,
            totalMrs: response.total_mrs || 0,
            totalDoctors: response.total_doctors || 0,
            totalHospitals: response.total_hospitals || 0,
            totalMedications: response.total_medications || 0,
            currentMonthRevenue: `₹${(response.current_month_revenue || 0).toLocaleString()}`,
            avgComplianceRate: response.avg_compliance_rate || 0,
          };
          this.medicineWisePatients = response.medicine_wise_patients || [];
          this.hqWisePatients = response.hq_wise_patients || [];
          this.managerHqWisePatients = response.manager_hq_wise_patients || [];
          this.drWisePatients = response.dr_wise_patients || [];
          this.isLoadingStats = false;
          this.checkLoadingState();
        },
        error: (error) => {
          console.error('Error fetching dashboard overview:', error);
          this.isLoadingStats = false;
          this.checkLoadingState();
        },
      });
  }

  fetchUsers() {
    this.isLoadingUsers = true;
    this.usersService.getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.users) {
            // Map all users and then slice to show only 4
            const allUsers = response.users.map((u: any) => ({
              id: u.id,
              name: u.name,
              email: u.email,
              role: u.role || 'USER', // Default role if missing
            }));

            // Display only first 4 users on dashboard
            this.users = allUsers.slice(0, 4);
          }
          this.isLoadingUsers = false;
          this.checkLoadingState();
        },
        error: (error) => {
          console.error('Error fetching users:', error);
          this.isLoadingUsers = false;
          this.checkLoadingState();
        },
      });
  }

  fetchTelecallers() {
    this.isLoadingTelecallers = true;
    this.telecallersService.getTelecallers(1, 100)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.users) {
            const allTelecallers = response.users.map((t) => {
              const programNames = t.programs ? t.programs.map(p => p.program_name) : [];
              return {
                id: t.id,
                user_id: t.user_id,
                name: t.name,
                programs: programNames,
                displayPrograms: programNames.slice(0, 2),
                morePrograms: Math.max(0, programNames.length - 2),
                patientsAssigned: t.no_of_patients || 0,
                programCount: t.no_of_programs || 0,
                totalSales: `₹${(t.total_net_amount || 0).toLocaleString()}`,
              };
            });

            // Display only first 4 telecallers on dashboard
            this.telecallers = allTelecallers.slice(0, 4);
            this.filteredTelecallers = [...this.telecallers];
          }
          this.isLoadingTelecallers = false;
          this.checkLoadingState();
        },
        error: (error) => {
          console.error('Error fetching telecallers:', error);
          this.isLoadingTelecallers = false;
          this.checkLoadingState();
        },
      });
  }

  // Get badge variant based on role
  getRoleVariant(
    role: string
  ): 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' {
    const roleUpper = (role || '').toUpperCase();
    if (roleUpper === 'ADMIN') return 'primary';
    if (roleUpper === 'TELECALLER') return 'danger';
    if (roleUpper === 'PROGRAM MANAGER') return 'warning';
    return 'secondary';
  }
}
